import type { Booking } from "@/types/Booking";
import { endTimeToMinutes } from "@/utils/bookingTime";

/**
 * Pure conflict-detection for the "Change Duration" / "Change Date & Time" UIs.
 *
 *   • `candidate` is the proposed time slot for the booking being modified.
 *   • `currentBooking` is the booking the user is changing — excluded by id so the slot
 *     never counts as "clashing with itself".
 *   • `otherBookings` is the live set of bookings on the relevant day/room. Each one is
 *     evaluated for overlap, with the buffer rules below.
 *
 * Rules (mirroring server `checkRoomBookingConflict`):
 *   • Core time overlap is always a clash (R7).
 *   • Same-member adjacency is allowed (R6) — if the candidate ends exactly at the
 *     existing booking's start OR starts exactly at its end, we skip the buffer check.
 *   • Cross-member: both sides' buffers are honoured (R5). If either booking
 *     `hasBuffers === true`, its core time is widened by 15 minutes either side; the
 *     two widened ranges must not overlap.
 *
 * Returns the FIRST clashing booking found (so callers can render a useful "clashes
 * with HH:MM …" message), or `null` when the candidate is free.
 */

export interface ConflictCandidate {
  /** HHmm */
  startTime: string;
  /** HHmm */
  endTime: string;
}

const BUFFER_MINUTES = 15;

const hhmmToMinutes = (hhmm: string): number => {
  const padded = hhmm.padStart(4, "0");
  return parseInt(padded.slice(0, 2), 10) * 60 + parseInt(padded.slice(2, 4), 10);
};

interface Range {
  start: number;
  end: number;
}

const buffered = (start: number, end: number, hasBuffers: boolean | undefined): Range => {
  if (!hasBuffers) return { start, end };
  return {
    start: Math.max(0, start - BUFFER_MINUTES),
    end: Math.min(24 * 60 - 1, end + BUFFER_MINUTES),
  };
};

const overlaps = (a: Range, b: Range): boolean => a.start < b.end && a.end > b.start;

export function findDurationConflict(
  candidate: ConflictCandidate,
  currentBooking: Pick<Booking, "id" | "memberId" | "hasBuffers" | "startTime" | "endTime">,
  otherBookings: ReadonlyArray<Booking>,
  // The current booking's organisation party (members + owner). When supplied, the org's own
  // bookings may butt back-to-back (buffer waived), exactly as the same member's (R6 parity with the
  // server). Omit for non-org bookings — behaviour is then unchanged.
  sameOrgMemberIds?: ReadonlySet<string>,
): Booking | null {
  const candStart = hhmmToMinutes(candidate.startTime);
  const candEnd = endTimeToMinutes(candidate.endTime, candidate.startTime);
  if (candEnd <= candStart) return null; // malformed candidate — caller should validate first

  const candHasBuffers = currentBooking.hasBuffers ?? true;
  const candCore: Range = { start: candStart, end: candEnd };

  for (const b of otherBookings) {
    // Never clash against yourself.
    if (b.id === currentBooking.id) continue;
    if (b.isCancelled) continue;

    const bStart = hhmmToMinutes(b.startTime);
    const bEnd = endTimeToMinutes(b.endTime, b.startTime);
    if (bEnd <= bStart) continue;
    const bCore: Range = { start: bStart, end: bEnd };

    // R7 — core overlap is always a clash, regardless of owner.
    if (overlaps(candCore, bCore)) {
      return b;
    }

    const isSameMember = b.memberId === currentBooking.memberId;
    const isSameOrg = sameOrgMemberIds?.has(b.memberId) ?? false;

    if (isSameMember || isSameOrg) {
      // R6 — same-member (or same-org) adjacency is allowed (touching is fine, buffers may overlap).
      // No further check needed once core is non-overlapping.
      continue;
    }

    // R5 — cross-member: honour both sides' buffers.
    const bHasBuffers = b.hasBuffers ?? true;
    const candBuffered = buffered(candStart, candEnd, candHasBuffers);
    const bBuffered = buffered(bStart, bEnd, bHasBuffers);
    if (overlaps(candBuffered, bBuffered)) {
      return b;
    }
  }
  return null;
}
